import { request } from "./client";

/**
 * Credential lifecycle as the server reports it. The client never derives this value: a browser
 * clock is not authoritative for expiry, and `pending` means the credential exists but is not yet
 * usable. Only `active` credentials may be rotated, replaced or revoked.
 */
export type IntegrationCredentialState = "active" | "pending" | "expired" | "revoked";

/** Metadata only. A one-time bearer value never appears in this shape. */
export interface IntegrationCredential {
  credentialId: string;
  providerCode: string;
  providerStatus: string;
  scopes: string[];
  notBefore: string;
  expiresAt: string;
  createdAt: string;
  revokedAt: string | null;
  state: IntegrationCredentialState;
}

export interface IntegrationCredentialPage {
  credentials: IntegrationCredential[];
}

/** The single response that carries a usable secret. Field name is part of the contract. */
export interface IssuedIntegrationCredential {
  credentialId: string;
  providerCode: string;
  authorization: string;
  scopes: string[];
  expiresAt: string;
}

export interface IssueIntegrationCredentialCommand {
  providerCode: string;
  scopes: string[];
  expiresAt: string;
}

export interface ReplaceIntegrationCredentialCommand {
  scopes: string[];
  expiresAt: string;
}

const base = "/admin/v1/integration-providers";
const credential = (providerCode: string, credentialId: string) =>
  `${base}/${encodeURIComponent(providerCode)}/credentials/${encodeURIComponent(credentialId)}`;

export const integrationCredentials = {
  list: () => request<IntegrationCredentialPage>(base),
  issue: (command: IssueIntegrationCredentialCommand) =>
    request<IssuedIntegrationCredential>(base, { method: "POST", body: command, csrf: true }),
  rotate: (providerCode: string, credentialId: string, command: ReplaceIntegrationCredentialCommand) =>
    request<IssuedIntegrationCredential>(`${credential(providerCode, credentialId)}/rotate`, {
      method: "POST",
      body: command,
      csrf: true,
    }),
  revoke: (providerCode: string, credentialId: string) =>
    request<void>(`${credential(providerCode, credentialId)}/revoke`, { method: "POST", csrf: true }),
  replace: (providerCode: string, credentialId: string, command: ReplaceIntegrationCredentialCommand) =>
    request<IssuedIntegrationCredential>(`${credential(providerCode, credentialId)}/replace`, {
      method: "POST",
      body: command,
      csrf: true,
    }),
};

export const canActOn = (state: IntegrationCredentialState): boolean => state === "active";
